import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { z } from "zod";
import type { Database } from "@/integrations/supabase/types";
import { requireActiveAuth as requireSupabaseAuth } from "@/lib/auth/requireActiveAuth";

function publicClient() {
  const url = process.env.SUPABASE_URL!;
  const key = process.env.SUPABASE_PUBLISHABLE_KEY!;
  return createClient<Database>(url, key, {
    auth: { persistSession: false, autoRefreshToken: false },
  });
}

async function adminClient() {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  return supabaseAdmin;
}

export type TrackSummary = {
  id: string;
  slug: string;
  title: string;
  subtitle: string | null;
  description: string | null;
  icon: string | null;
  color: string;
  order_index: number;
  lesson_count: number;
  total_minutes: number;
};

export const listTracks = createServerFn({ method: "GET" }).handler(
  async (): Promise<TrackSummary[]> => {
    const sb = publicClient();
    const { data: tracks, error } = await sb
      .from("academy_tracks")
      .select("id, slug, title, subtitle, description, icon, color, order_index")
      .eq("published", true)
      .order("order_index");
    if (error) throw new Error(error.message);

    const { data: lessons } = await sb
      .from("academy_lessons")
      .select("track_id, estimated_minutes")
      .eq("published", true)
      .overlaps("audience", ["public", "client"]);

    const stats = new Map<string, { count: number; minutes: number }>();
    for (const l of lessons ?? []) {
      const s = stats.get(l.track_id) ?? { count: 0, minutes: 0 };
      s.count += 1;
      s.minutes += l.estimated_minutes ?? 0;
      stats.set(l.track_id, s);
    }

    return (tracks ?? []).map((t) => ({
      ...t,
      lesson_count: stats.get(t.id)?.count ?? 0,
      total_minutes: stats.get(t.id)?.minutes ?? 0,
    }));
  },
);

export const getTrackBySlug = createServerFn({ method: "GET" })
  .inputValidator((d: { slug: string }) => z.object({ slug: z.string().min(1).max(120) }).parse(d))
  .handler(async ({ data }) => {
    const sb = publicClient();
    const { data: track, error } = await sb
      .from("academy_tracks")
      .select("*")
      .eq("slug", data.slug)
      .eq("published", true)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!track) return null;
    const { data: lessons } = await sb
      .from("academy_lessons")
      .select("id, slug, title, subtitle, excerpt, lesson_type, estimated_minutes, order_index")
      .eq("track_id", track.id)
      .eq("published", true)
      .overlaps("audience", ["public", "client"])
      .order("order_index");
    return { track, lessons: lessons ?? [] };
  });

export const getLesson = createServerFn({ method: "GET" })
  .inputValidator((d: { trackSlug: string; lessonSlug: string }) =>
    z
      .object({
        trackSlug: z.string().min(1).max(120),
        lessonSlug: z.string().min(1).max(120),
      })
      .parse(d),
  )
  .handler(async ({ data }) => {
    const sb = publicClient();
    const { data: track } = await sb
      .from("academy_tracks")
      .select("id, slug, title, color")
      .eq("slug", data.trackSlug)
      .eq("published", true)
      .maybeSingle();
    if (!track) return null;
    const { data: lesson } = await sb
      .from("academy_lessons")
      .select("*")
      .eq("track_id", track.id)
      .eq("slug", data.lessonSlug)
      .eq("published", true)
      .overlaps("audience", ["public", "client"])
      .maybeSingle();
    if (!lesson) return null;
    const { data: siblings } = await sb
      .from("academy_lessons")
      .select("slug, title, order_index")
      .eq("track_id", track.id)
      .eq("published", true)
      .overlaps("audience", ["public", "client"])
      .order("order_index");
    const list = siblings ?? [];
    const idx = list.findIndex((l) => l.slug === lesson.slug);
    return {
      track,
      lesson,
      prev: idx > 0 ? list[idx - 1] : null,
      next: idx >= 0 && idx < list.length - 1 ? list[idx + 1] : null,
    };
  });

export const getFeaturedLesson = createServerFn({ method: "GET" }).handler(async () => {
  const sb = publicClient();
  const { data } = await sb
    .from("academy_lessons")
    .select("id, slug, title, subtitle, excerpt, estimated_minutes, track_id")
    .eq("published", true)
    .eq("featured", true)
    .overlaps("audience", ["public", "client"])
    .order("updated_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (!data) return null;
  const { data: track } = await sb
    .from("academy_tracks")
    .select("slug, title, color")
    .eq("id", data.track_id)
    .maybeSingle();
  return { lesson: data, track };
});

export const searchLessons = createServerFn({ method: "GET" })
  .inputValidator((d: { q: string }) => z.object({ q: z.string().max(200) }).parse(d))
  .handler(async ({ data }) => {
    const q = data.q.trim();
    if (q.length < 2) return [];
    const sb = publicClient();
    const pattern = `%${q.replace(/[%_]/g, "")}%`;
    const { data: rows } = await sb
      .from("academy_lessons")
      .select("id, slug, title, excerpt, track_id")
      .eq("published", true)
      .overlaps("audience", ["public", "client"])
      .or(`title.ilike.${pattern},excerpt.ilike.${pattern},body.ilike.${pattern}`)
      .limit(10);
    if (!rows?.length) return [];
    const trackIds = [...new Set(rows.map((r) => r.track_id))];
    const { data: tracks } = await sb
      .from("academy_tracks")
      .select("id, slug, title")
      .in("id", trackIds);
    const tMap = new Map((tracks ?? []).map((t) => [t.id, t]));
    return rows.map((r) => ({
      id: r.id,
      slug: r.slug,
      title: r.title,
      excerpt: r.excerpt,
      track: tMap.get(r.track_id) ?? null,
    }));
  });

export const submitQuestion = createServerFn({ method: "POST" })
  .inputValidator((d: { email: string; question: string; name?: string; category?: string }) =>
    z
      .object({
        email: z.string().trim().email().max(255),
        question: z.string().trim().min(10).max(2000),
        name: z.string().trim().max(120).optional(),
        category: z.string().trim().max(60).optional(),
      })
      .parse(d),
  )
  .handler(async ({ data }) => {
    const sb = publicClient();
    const prefix = [
      data.name ? `Name: ${data.name}` : null,
      data.category ? `Category: ${data.category}` : null,
    ].filter(Boolean).join(" · ");
    const body = prefix ? `[${prefix}]\n\n${data.question}` : data.question;
    const { error } = await sb.from("academy_questions").insert({
      submitted_by_email: data.email,
      question: body,
    });
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// ---------- Glossary ----------
export const listGlossary = createServerFn({ method: "GET" }).handler(async () => {
  const sb = publicClient();
  const { data, error } = await sb
    .from("academy_glossary")
    .select("id, term, slug, short_definition, category, featured, view_count")
    .order("term");
  if (error) throw new Error(error.message);
  return data ?? [];
});

export const getGlossaryTerm = createServerFn({ method: "GET" })
  .inputValidator((d: { slug: string }) => z.object({ slug: z.string().min(1).max(120) }).parse(d))
  .handler(async ({ data }) => {
    const sb = publicClient();
    const { data: term, error } = await sb
      .from("academy_glossary")
      .select("*")
      .eq("slug", data.slug)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!term) return null;
    await sb.rpc("increment_glossary_view", { _id: term.id });
    let related: Array<{ id: string; term: string; slug: string }> = [];
    if (term.related_terms?.length) {
      const { data: rel } = await sb
        .from("academy_glossary")
        .select("id, term, slug")
        .in("id", term.related_terms);
      related = rel ?? [];
    }
    let lessons: Array<{ id: string; slug: string; title: string; track_id: string }> = [];
    if (term.related_lessons?.length) {
      const { data: les } = await sb
        .from("academy_lessons")
        .select("id, slug, title, track_id")
        .in("id", term.related_lessons)
        .eq("published", true);
      lessons = les ?? [];
    }
    return { term, related, lessons };
  });

// ---------- Family Library ----------
export const listLibrary = createServerFn({ method: "GET" }).handler(async () => {
  const sb = publicClient();
  const { data, error } = await sb
    .from("family_library")
    .select("*")
    .order("order_index");
  if (error) throw new Error(error.message);
  return data ?? [];
});

export const recordLibraryDownload = createServerFn({ method: "POST" })
  .inputValidator((d: { id: string; email?: string }) =>
    z
      .object({
        id: z.string().uuid(),
        email: z.string().trim().email().max(255).optional().or(z.literal("")),
      })
      .parse(d),
  )
  .handler(async ({ data }) => {
    const sb = publicClient();
    await sb.rpc("increment_library_download", { _id: data.id });
    if (data.email) {
      await sb.from("contact_messages").insert({
        full_name: "Library download",
        email: data.email,
        subject: "library-email-gate",
        body: `Email captured at library download of ${data.id}`,
      });
    }
    return { ok: true };
  });

// ---------- Ask Currency Thoughts ----------
export const listAnsweredQuestions = createServerFn({ method: "GET" }).handler(async () => {
  const sb = publicClient();
  const { data, error } = await sb
    .from("academy_questions_public")
    .select("id, question, answer, featured, created_at, track_id")
    .order("created_at", { ascending: false })
    .limit(40);
  if (error) throw new Error(error.message);
  return data ?? [];
});

export const getQuestion = createServerFn({ method: "GET" })
  .inputValidator((d: { id: string }) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data }) => {
    const sb = publicClient();
    const { data: q, error } = await sb
      .from("academy_questions_public")
      .select("id, question, answer, featured, created_at, track_id")
      .eq("id", data.id)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!q) return null;
    const questionId = q.id as string;
    const admin = await adminClient();
    const { count: up } = await admin
      .from("academy_question_votes")
      .select("id", { count: "exact", head: true })
      .eq("question_id", questionId)
      .eq("vote", "up");
    const { count: down } = await admin
      .from("academy_question_votes")
      .select("id", { count: "exact", head: true })
      .eq("question_id", questionId)
      .eq("vote", "down");
    return { question: q, up: up ?? 0, down: down ?? 0 };
  });

export const voteQuestion = createServerFn({ method: "POST" })
  .inputValidator((d: { id: string; sessionId: string; vote: "up" | "down" }) =>
    z
      .object({
        id: z.string().uuid(),
        sessionId: z.string().min(8).max(64),
        vote: z.enum(["up", "down"]),
      })
      .parse(d),
  )
  .handler(async ({ data }) => {
    const sb = await adminClient();
    const { error } = await sb
      .from("academy_question_votes")
      .upsert(
        { question_id: data.id, session_id: data.sessionId, vote: data.vote },
        { onConflict: "question_id,session_id" },
      );
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const mergeSessionProgress = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { sessionId: string }) =>
    z.object({ sessionId: z.string().min(1).max(64) }).parse(d),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { error } = await supabase
      .from("academy_progress")
      .update({ user_id: userId, session_id: null })
      .eq("session_id", data.sessionId)
      .is("user_id", null);
    if (error) throw new Error(error.message);
    return { ok: true };
  });
/** Anonymous (session-scoped) progress reads/writes go through the server with a
 * service-role client, so no anon RLS policy keyed on an unverifiable session_id
 * is required. Signed-in users continue to use their own auth.uid()-scoped policies. */
export const getSessionProgress = createServerFn({ method: "POST" })
  .inputValidator((d: { sessionId: string; lessonIds: string[] }) =>
    z
      .object({
        sessionId: z.string().min(8).max(64),
        lessonIds: z.array(z.string().uuid()).max(500),
      })
      .parse(d),
  )
  .handler(async ({ data }): Promise<string[]> => {
    if (data.lessonIds.length === 0) return [];
    const sb = await adminClient();
    const { data: rows, error } = await sb
      .from("academy_progress")
      .select("lesson_id")
      .eq("session_id", data.sessionId)
      .in("lesson_id", data.lessonIds)
      .not("completed_at", "is", null);
    if (error) throw new Error(error.message);
    return (rows ?? []).map((r) => r.lesson_id);
  });

export const completeSessionLesson = createServerFn({ method: "POST" })
  .inputValidator((d: { sessionId: string; lessonId: string }) =>
    z
      .object({ sessionId: z.string().min(8).max(64), lessonId: z.string().uuid() })
      .parse(d),
  )
  .handler(async ({ data }) => {
    const sb = await adminClient();
    const completed_at = new Date().toISOString();
    const { data: existing } = await sb
      .from("academy_progress")
      .select("id")
      .eq("session_id", data.sessionId)
      .eq("lesson_id", data.lessonId)
      .maybeSingle();
    if (existing) {
      const { error } = await sb
        .from("academy_progress")
        .update({ status: "complete", completed_at })
        .eq("id", existing.id);
      if (error) throw new Error(error.message);
      return { ok: true };
    }
    const { error } = await sb.from("academy_progress").insert({
      session_id: data.sessionId,
      lesson_id: data.lessonId,
      status: "complete",
      completed_at,
    } as never);
    if (error) throw new Error(error.message);
    return { ok: true };
  });
